import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { ArrowLeft, CheckCircle, FileText, ClipboardCheck, ChevronDown, ChevronUp } from 'lucide-react';
import Spinner from './Spinner.js';
import MICPreviewContent from './MICPreviewContent.js';
import PCPreviewContent from './PCPreviewContent.js';

const ReceivedDetail = ({ db }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receivedData, setReceivedData] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    pc: true,
    mic: true,
    qc: true
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const docSnap = await getDoc(doc(db, "inwards", id));
        if (!docSnap.exists()) {
          alert("Received record not found");
          navigate('/ops/received');
          return;
        }

        const data = { id: docSnap.id, ...docSnap.data() };
        setReceivedData(data);

        if (data.customerID) {
          const customerSnap = await getDoc(doc(db, "customers", data.customerID));
          if (customerSnap.exists()) {
            setCustomer(customerSnap.data());
          }
        }

        const warehouseSnap = await getDocs(collection(db, "warehouses"));
        setWarehouses(warehouseSnap.docs.map((warehouseDoc) => ({ id: warehouseDoc.id, ...warehouseDoc.data() })));
      } catch (error) {
        console.error("Error fetching received record:", error);
        alert("Failed to load received record.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, id, navigate]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (value) => {
    if (!value) return '---';
    try {
      const date = value.toDate ? value.toDate() : new Date(value);
      return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return '---';
    }
  };

  if (loading || !receivedData) return <Spinner />;

  const isInterbranchMic = receivedData.typeOfMIC === 'Interbranch Transfer';
  const micPreviewData = {
    ...receivedData,
    components: receivedData.micComponents || receivedData.components || []
  };
  const qcPreviewFile = (receivedData.qcFiles || []).find((file) => (file.type || '').toLowerCase() === 'application/pdf')
    || (receivedData.qcFiles || [])[0]
    || null;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <div style={toolbarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate('/ops/received')} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 style={{ margin: 0 }}>Received Record: {receivedData.micNo || receivedData.inwardNo}</h2>
            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
              Final inward QC completion record.
            </p>
          </div>
        </div>
        <div style={statusPillStyle}>
          <CheckCircle size={14} /> QC COMPLETED
        </div>
      </div>

      <div style={pageCardStyle}>
        <div style={accordionStyle}>
          <button onClick={() => toggleSection('summary')} style={accordionBtnStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ClipboardCheck size={18} color="#2563eb" /> Received Summary
            </div>
            {expandedSections.summary ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSections.summary && (
            <div style={accordionContentStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                <div><label style={labelStyle}>Receipt Date</label><p style={valueStyle}>{formatDate(receivedData.qcCompletedAt || receivedData.updatedAt)}</p></div>
                <div><label style={labelStyle}>MIC No.</label><p style={valueStyle}>{receivedData.micNo || '---'}</p></div>
                <div><label style={labelStyle}>Reference Document</label><p style={valueStyle}>{receivedData.referenceNo || '---'}</p></div>
                <div><label style={labelStyle}>Warehouse</label><p style={valueStyle}>{receivedData.warehouse || '---'}</p></div>
                <div><label style={labelStyle}>Customer / Supplier</label><p style={valueStyle}>{receivedData.customerName || receivedData.supplierName || '---'}</p></div>
                <div><label style={labelStyle}>QC Status</label><p style={valueStyle}>QC Completed</p></div>
              </div>
            </div>
          )}
        </div>

        {!isInterbranchMic && (
          <div style={accordionStyle}>
            <button onClick={() => toggleSection('pc')} style={accordionBtnStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText size={18} color="#2563eb" /> PC Detail
              </div>
              {expandedSections.pc ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.pc && (
              <div style={accordionContentStyle}>
                <PCPreviewContent pcData={receivedData} />
              </div>
            )}
          </div>
        )}

        <div style={accordionStyle}>
          <button onClick={() => toggleSection('mic')} style={accordionBtnStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileText size={18} color="#2563eb" /> MIC Detail
            </div>
            {expandedSections.mic ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSections.mic && (
            <div style={accordionContentStyle}>
              <MICPreviewContent
                micData={micPreviewData}
                customer={customer}
                warehouses={warehouses}
              />

              {(receivedData.mic_completionRemarks || (receivedData.mic_completionFiles && receivedData.mic_completionFiles.length > 0)) && (
                <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>MIC Remarks</div>
                    <div style={detailsBoxStyle}>{receivedData.mic_completionRemarks || '---'}</div>
                  </div>

                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Uploaded MIC Files</div>
                    {(receivedData.mic_completionFiles && receivedData.mic_completionFiles.length > 0) ? (
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {receivedData.mic_completionFiles.map((file, index) => (
                          <a
                            key={`${file.url || file.name || 'mic-file'}_${index}`}
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                            style={attachmentCardStyle}
                          >
                            <FileText size={18} color="#64748b" />
                            <div style={{ overflow: 'hidden' }}>
                              <div style={attachmentNameStyle}>{file.name || 'Attachment'}</div>
                              <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div style={detailsBoxStyle}>No files attached.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={accordionStyle}>
          <button onClick={() => toggleSection('qc')} style={accordionBtnStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ClipboardCheck size={18} color="#2563eb" /> QC Detail
            </div>
            {expandedSections.qc ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {expandedSections.qc && (
            <div style={accordionContentStyle}>
              {qcPreviewFile && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>QC Scan Preview</div>
                  {(qcPreviewFile.type || '').toLowerCase() === 'application/pdf' ? (
                    <iframe
                      src={qcPreviewFile.url}
                      title="QC PDF Preview"
                      style={pdfPreviewStyle}
                    />
                  ) : (
                    <a
                      href={qcPreviewFile.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ ...attachmentCardStyle, minHeight: '64px' }}
                    >
                      <FileText size={18} color="#64748b" />
                      <div style={{ overflow: 'hidden' }}>
                        <div style={attachmentNameStyle}>{qcPreviewFile.name || 'QC Attachment'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>Preview not available for this file type. Click to open.</div>
                      </div>
                    </a>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div><div style={metaLabelStyle}>Vehicle Type</div><div style={metaValueStyle}>{receivedData.vehicleType || '---'}</div></div>
                <div><div style={metaLabelStyle}>Transport</div><div style={metaValueStyle}>{receivedData.transportName || receivedData.transport || '---'}</div></div>
                <div><div style={metaLabelStyle}>Weight (in kgs)</div><div style={metaValueStyle}>{receivedData.weightInKgs || '---'}</div></div>
                <div><div style={metaLabelStyle}>Missing / Damages</div><div style={metaValueStyle}>{receivedData.missingDamages || '---'}</div></div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>QC Remarks</div>
                  <div style={detailsBoxStyle}>{receivedData.qcRemarks || '---'}</div>
                </div>

                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Uploaded QC Files</div>
                  {(receivedData.qcFiles && receivedData.qcFiles.length > 0) ? (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {receivedData.qcFiles.map((file, index) => (
                        <a
                          key={`${file.url || file.name || 'received-file'}_${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={attachmentCardStyle}
                        >
                          <FileText size={18} color="#64748b" />
                          <div style={{ overflow: 'hidden' }}>
                            <div style={attachmentNameStyle}>{file.name || 'Attachment'}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div style={detailsBoxStyle}>No files attached.</div>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Vehicle Image Proof</div>
                  {(receivedData.vehicleImageProofFiles && receivedData.vehicleImageProofFiles.length > 0) ? (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {receivedData.vehicleImageProofFiles.map((file, index) => (
                        <a
                          key={`${file.url || file.name || 'vehicle-proof'}_${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={attachmentCardStyle}
                        >
                          <FileText size={18} color="#64748b" />
                          <div style={{ overflow: 'hidden' }}>
                            <div style={attachmentNameStyle}>{file.name || 'Attachment'}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div style={detailsBoxStyle}>No files attached.</div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Missing / Damages Files</div>
                  {(receivedData.missingDamageFiles && receivedData.missingDamageFiles.length > 0) ? (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {receivedData.missingDamageFiles.map((file, index) => (
                        <a
                          key={`${file.url || file.name || 'missing-damage'}_${index}`}
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          style={attachmentCardStyle}
                        >
                          <FileText size={18} color="#64748b" />
                          <div style={{ overflow: 'hidden' }}>
                            <div style={attachmentNameStyle}>{file.name || 'Attachment'}</div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>{file.type || 'file'}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <div style={detailsBoxStyle}>No files attached.</div>
                  )}
                </div>
              </div>

              {receivedData.inwardQcChecklist && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>Checklist</div>
                  <div style={checklistWrapStyle}>
                    {[
                      ['informedToHo', 'INFORMED TO HO FOR MISSING AND DAMAGES (WHATSAPP / SLACK)'],
                      ['paperSharedToOffice', 'PAPER SHARED TO OFFICE (WHATSAPP / SLACK)'],
                      ['damagePartsSentForMaintenance', 'DAMAGE PARTS SENT FOR MAINTENANCE (IF ANY)'],
                      ['invoicePapersSentToAccounts', 'INVOICE / PAPERS SENT TO ACCOUNTS']
                    ].map(([key, label]) => (
                      <div key={key} style={completedChecklistItemStyle}>
                        <span style={{ fontWeight: '700', color: receivedData.inwardQcChecklist?.[key] ? '#166534' : '#64748b' }}>
                          {receivedData.inwardQcChecklist?.[key] ? 'Yes' : 'No'}
                        </span>
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const toolbarStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' };
const backBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' };
const pageCardStyle = { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' };
const statusPillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: '#dcfce7',
  color: '#166534',
  padding: '8px 14px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: '800'
};
const accordionStyle = { borderBottom: '1px solid #f1f5f9' };
const accordionBtnStyle = {
  width: '100%',
  padding: '15px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '600',
  color: '#334155',
  textAlign: 'left'
};
const accordionContentStyle = { padding: '20px', backgroundColor: '#fdfdfd', borderTop: '1px solid #f8fafc' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const valueStyle = { fontSize: '14px', color: '#1e293b', fontWeight: '500', marginBottom: '0' };
const detailsBoxStyle = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '12px',
  minHeight: '90px',
  whiteSpace: 'pre-wrap',
  color: '#0f172a',
  fontSize: '13px'
};
const attachmentCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  textDecoration: 'none',
  color: '#0f172a',
  backgroundColor: '#ffffff'
};
const attachmentNameStyle = {
  fontSize: '13px',
  fontWeight: '700',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
const checklistWrapStyle = {
  display: 'grid',
  gap: '10px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  padding: '14px'
};
const completedChecklistItemStyle = {
  display: 'grid',
  gridTemplateColumns: '56px 1fr',
  gap: '10px',
  fontSize: '13px',
  color: '#1e293b'
};
const metaLabelStyle = {
  fontSize: '11px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  marginBottom: '6px'
};
const metaValueStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#0f172a'
};
const pdfPreviewStyle = {
  width: '100%',
  height: '640px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  backgroundColor: '#ffffff'
};

export default ReceivedDetail;
